import React, { useEffect, useMemo, useRef, useState } from "react";
import { AsyncButton } from "../AsyncButton";
import "../../styles/ChatbotAssistant.scss";
import { adminService } from "../../services/admin.service";
import { CHATBOT_LABELS, CHATBOT_SETTINGS } from "../../config/chatbot.config";
import { AssistantIcon } from "./AssistantIcon";
import { PublicGuestAvatarIcon } from "./PublicGuestAvatarIcon";
import { PublicCaptchaField } from "../public/PublicCaptchaField";
import { useUser } from "../../context/UserContext";
import { getAvatarUrlOrNull, getUserInitials } from "../../utils/avatar.utils";
import {
  CHATBOT_PUBLIC_MAX_INTERACTIONS,
  getOrCreatePublicChatbotSession,
  incrementPublicChatbotInteraction,
  type PublicChatbotCookie,
} from "../../utils/chatbotPublicSession";
import { Maximize2, Minimize2, X } from "lucide-react";

interface ChatCitation {
  source: string;
  title?: string;
  snippet?: string;
}

interface Message {
  id: string;
  text: string;
  sender: "user" | "assistant";
  timestamp: Date;
  feedback?: "positive" | "negative" | null;
  messageId?: string;
  citations?: ChatCitation[];
  latencyMs?: number;
}

interface ChatbotAssistantProps {
  onClose?: () => void;
  usageMode?: "authenticated" | "public";
  sizeMode?: "default" | "expanded";
  onToggleSize?: () => void;
}

const MAX_MESSAGE_LENGTH = CHATBOT_SETTINGS.maxMessageLength;

export const ChatbotAssistant: React.FC<ChatbotAssistantProps> = ({
  onClose,
  usageMode = "authenticated",
  sizeMode = "default",
  onToggleSize,
}) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const { userProfile } = useUser();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "welcome",
      text: CHATBOT_LABELS.welcomeMessage,
      sender: "assistant",
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [publicSession, setPublicSession] =
    useState<PublicChatbotCookie | null>(null);
  const [publicLimitReached, setPublicLimitReached] = useState(false);
  const [publicCaptchaToken, setPublicCaptchaToken] = useState<string | null>(
    null,
  );
  const [publicCaptchaResetTrigger, setPublicCaptchaResetTrigger] = useState(0);
  const [publicCaptchaRequired, setPublicCaptchaRequired] = useState(false);
  const [registeredLimitReached, setRegisteredLimitReached] = useState(false);
  const [userAvatarLoadFailed, setUserAvatarLoadFailed] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (usageMode !== "public") return;
    const s = getOrCreatePublicChatbotSession();
    setPublicSession(s);
    if (
      s.interactionCount >= CHATBOT_SETTINGS.publicCaptchaAfterInteractions
    ) {
      setPublicCaptchaRequired(true);
    }
    if (s.interactionCount >= CHATBOT_PUBLIC_MAX_INTERACTIONS) {
      setPublicLimitReached(true);
    }
  }, [usageMode]);

  const interactionLimitReached =
    (usageMode === "public" && publicLimitReached) ||
    (usageMode === "authenticated" && registeredLimitReached);

  useEffect(() => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }, 100);
    });
  }, [messages, isTyping]);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  const historyPayload = useMemo(
    () =>
      messages
        .slice(-CHATBOT_SETTINGS.maxHistoryMessages)
        .filter((msg) => msg.sender === "user" || msg.sender === "assistant")
        .map((msg) => ({
          role: msg.sender,
          content: msg.text,
        })),
    [messages],
  );

  const sendText = async (text: string) => {
    const textToSend = text.trim();
    if (!textToSend) return;
    if (usageMode === "public" && !publicSession) return;
    if (interactionLimitReached) {
      setError(CHATBOT_LABELS.limitReachedMessage);
      return;
    }

    setError(null);
    const userMessage: Message = {
      id: Date.now().toString(),
      text: textToSend,
      sender: "user",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsTyping(true);

    try {
      const start = Date.now();
      const response = await adminService.chatbotQuery({
        message: textToSend,
        sessionId,
        history: historyPayload,
        ...(usageMode === "public" && publicSession
          ? { publicSessionId: publicSession.sessionId }
          : {}),
        ...(usageMode === "public" && publicCaptchaToken
          ? { captchaToken: publicCaptchaToken }
          : {}),
      });

      setSessionId(response.sessionId);
      setMessages((prev) => [
        ...prev,
        {
          id: response.messageId || (Date.now() + 1).toString(),
          text: response.answer,
          sender: "assistant",
          timestamp: new Date(),
          messageId: response.messageId,
          citations: response.citations ?? [],
          latencyMs: response.latencyMs ?? Date.now() - start,
        },
      ]);

      if (usageMode === "public") {
        if (
          response.outOfScope &&
          response.answer.trim() === CHATBOT_LABELS.limitReachedMessage
        ) {
          setPublicLimitReached(true);
        } else {
          const next = incrementPublicChatbotInteraction();
          if (next) {
            setPublicSession(next);
            if (
              next.interactionCount >=
              CHATBOT_SETTINGS.publicCaptchaAfterInteractions
            ) {
              setPublicCaptchaRequired(true);
            }
            if (next.interactionCount >= CHATBOT_PUBLIC_MAX_INTERACTIONS) {
              setPublicLimitReached(true);
            }
          }
        }
        // Turnstile tokens are short-lived and single-use; force a fresh token
        // for the next protected public request once CAPTCHA is active.
        if (publicCaptchaRequired) {
          setPublicCaptchaToken(null);
          setPublicCaptchaResetTrigger((v) => v + 1);
        }
      }
    } catch (err: any) {
      // API client normalizes axios errors to ApiError { status, details, code }.
      // Keep backward compatibility with raw axios-like errors as a fallback.
      const status = err?.status ?? err?.response?.status;
      const code = err?.details?.code ?? err?.code ?? err?.response?.data?.code;
      const msg =
        err?.details?.message ||
        err?.message ||
        err?.response?.data?.message ||
        CHATBOT_LABELS.limitReachedMessage;

      if (status === 400 && code === "CHATBOT_MESSAGE_TOO_LONG") {
        setMessages((prev) => prev.slice(0, -1));
        setInputValue(userMessage.text);
        setError(msg);
        return;
      }
      if (
        status === 400 &&
        (code === "CHATBOT_CAPTCHA_REQUIRED" || code === "CHATBOT_CAPTCHA_FAILED")
      ) {
        setMessages((prev) => prev.slice(0, -1));
        setInputValue(userMessage.text);
        setPublicCaptchaRequired(true);
        setPublicCaptchaToken(null);
        setPublicCaptchaResetTrigger((v) => v + 1);
        setError(
          code === "CHATBOT_CAPTCHA_REQUIRED"
            ? CHATBOT_LABELS.captchaRequiredMessage
            : CHATBOT_LABELS.captchaFailedMessage,
        );
        return;
      }

      if (
        status === 429 &&
        (code === "CHATBOT_PUBLIC_LIMIT" || code === "CHATBOT_REGISTERED_LIMIT")
      ) {
        setMessages((prev) => prev.slice(0, -1));
        setInputValue(userMessage.text);
        if (code === "CHATBOT_PUBLIC_LIMIT") setPublicLimitReached(true);
        else setRegisteredLimitReached(true);
        setError(msg);
        return;
      }

      const isAuthError = status === 401 || status === 403;
      const fallbackMessage = isAuthError
        ? "Please sign in to use the AI assistant. Some features require authentication."
        : "Sorry, I could not reach the assistant right now. Please try again shortly.";
      setError(err?.response?.data?.message || fallbackMessage);
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 2).toString(),
          text: fallbackMessage,
          sender: "assistant",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSendMessage = async () => {
    await sendText(inputValue);
  };

  const handleQuickQuestion = async (question: string) => {
    await sendText(question);
  };

  const handleFeedback = async (
    messageId: string | undefined,
    feedbackType: "positive" | "negative",
  ) => {
    if (!messageId || !sessionId) return;
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId && msg.sender === "assistant"
          ? { ...msg, feedback: feedbackType }
          : msg,
      ),
    );
    try {
      await adminService.chatbotFeedback({
        sessionId,
        messageId,
        feedback: feedbackType,
      });
    } catch {
      // Keep optimistic UI feedback even if request fails.
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value.slice(0, MAX_MESSAGE_LENGTH));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    const trimmedValue = inputValue.trim();
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (trimmedValue && trimmedValue.length <= MAX_MESSAGE_LENGTH) {
        void handleSendMessage();
      }
    }
  };

  const isAtCharacterLimit = inputValue.length >= MAX_MESSAGE_LENGTH;
  const userAvatarUrl = userProfile ? getAvatarUrlOrNull(userProfile) : null;
  const userInitials = getUserInitials({
    firstName: userProfile?.firstName,
    lastName: userProfile?.lastName,
    email: userProfile?.email,
  });

  return (
    <div className="chatbot-assistant">
      <div className="chatbot-header">
        <div className="chatbot-avatar">
          <AssistantIcon width={40} height={40} />
        </div>
        <div className="chatbot-info">
          <h3 id="chatbot-header-title">{CHATBOT_LABELS.title}</h3>
          <p>{CHATBOT_LABELS.subtitle}</p>
        </div>
        <div className="chatbot-header-actions">
          {onToggleSize && (
            <button
              type="button"
              className="chatbot-size-btn"
              onClick={onToggleSize}
              aria-label={
                sizeMode === "expanded"
                  ? "Collapse chatbot size"
                  : "Expand chatbot size"
              }
              title={sizeMode === "expanded" ? "Collapse" : "Expand"}
            >
              {sizeMode === "expanded" ? (
                <Minimize2 size={18} absoluteStrokeWidth={true} />
              ) : (
                <Maximize2 size={18} absoluteStrokeWidth={true} />
              )}
            </button>
          )}
          {onClose && (
            <button
              ref={closeButtonRef}
              type="button"
              className="close-btn"
              onClick={onClose}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClose();
                }
              }}
              aria-label="Close chatbot"
            >
              <X size={20} absoluteStrokeWidth={true} />
            </button>
          )}
        </div>
      </div>

      {(error || interactionLimitReached) && (
        <div className="chatbot-error" role="alert">
          {error || CHATBOT_LABELS.limitReachedMessage}
        </div>
      )}

      <div
        className="chatbot-messages"
        role="log"
        aria-live="polite"
        aria-label="Chat messages"
      >
        {messages.map((message, index) => (
          <React.Fragment key={message.id}>
            <div className={`message ${message.sender}`}>
              <div className="message-avatar" aria-hidden="true">
                {message.sender === "assistant" ? (
                  <AssistantIcon width={32} height={32} />
                ) : (
                  <>
                    {userAvatarUrl && !userAvatarLoadFailed ? (
                      <img
                        src={userAvatarUrl}
                        alt=""
                        onError={() => setUserAvatarLoadFailed(true)}
                      />
                    ) : usageMode === "public" ? (
                      <span className="chatbot-public-user-avatar-icon">
                        <PublicGuestAvatarIcon size={21} />
                      </span>
                    ) : (
                      <span>{userInitials}</span>
                    )}
                  </>
                )}
              </div>
              <div className="message-content">
                <div className="message-text">
                  {message.text.split("\n").map((line, idx) => (
                    <React.Fragment key={idx}>
                      {line}
                      {idx < message.text.split("\n").length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
                <div className="message-time">
                  {message.timestamp.toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  })}
                </div>

                {message.citations && message.citations.length > 0 && (
                  <div className="citations">
                    <div className="citations-title">Sources</div>
                    <ul>
                      {message.citations.map((c, idx) => (
                        <li key={`${message.id}-cit-${idx}`}>
                          <span className="citation-title">
                            {c.title || "Document"}
                          </span>
                          {c.snippet && (
                            <span className="citation-snippet">
                              {" "}
                              — {c.snippet}
                            </span>
                          )}
                          {c.source && (
                            <a href={c.source} target="_blank" rel="noreferrer">
                              View
                            </a>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {message.sender === "assistant" && message.id !== "welcome" && (
                  <div className="message-footer">
                    <div className="message-feedback">
                      <button
                        className={`feedback-btn ${message.feedback === "positive" ? "active" : ""}`}
                        onClick={() =>
                          handleFeedback(
                            message.messageId || message.id,
                            "positive",
                          )
                        }
                        aria-label="Thumbs up - This was helpful"
                        title="This was helpful"
                        type="button"
                      >
                        <span aria-hidden="true">👍</span>
                      </button>
                      <button
                        className={`feedback-btn ${message.feedback === "negative" ? "active" : ""}`}
                        onClick={() =>
                          handleFeedback(
                            message.messageId || message.id,
                            "negative",
                          )
                        }
                        aria-label="Thumbs down - This was not helpful"
                        title="This was not helpful"
                        type="button"
                      >
                        <span aria-hidden="true">👎</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </React.Fragment>
        ))}

        {isTyping && (
          <div className="message assistant" role="status" aria-live="polite">
            <div className="message-avatar" aria-hidden="true">
              <AssistantIcon width={32} height={32} />
            </div>
            <div className="message-content">
              <div
                className="typing-indicator"
                aria-label="AI assistant is typing"
              >
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chatbot-input">
        <div className="input-container">
          <div className="input-field-wrap">
            <label htmlFor="chatbot-message-input" className="sr-only">
              Message
            </label>
            <textarea
              id="chatbot-message-input"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyPress}
              placeholder={CHATBOT_LABELS.placeholder}
              rows={2}
              maxLength={MAX_MESSAGE_LENGTH}
              className="message-input"
              aria-invalid={isAtCharacterLimit}
              aria-describedby={
                isAtCharacterLimit ? "chatbot-message-length-error" : undefined
              }
            />
            {isAtCharacterLimit && (
              <span
                id="chatbot-message-length-error"
                className="text-danger fs-7 mt-1"
                role="alert"
              >
                {CHATBOT_LABELS.messageLengthError}
              </span>
            )}
          </div>
          <AsyncButton
            type="button"
            onClick={(e) => {
              e.preventDefault();
              void handleSendMessage();
            }}
            loading={isTyping}
            disabled={
              !inputValue.trim() ||
              interactionLimitReached ||
              (usageMode === "public" &&
                publicCaptchaRequired &&
                !publicCaptchaToken)
            }
            className="send-button"
            aria-label="Send message"
          >
            <span aria-hidden="true">📤</span>
          </AsyncButton>
        </div>
        {usageMode === "public" && publicCaptchaRequired && (
          <div className="chatbot-captcha mt-2">
            <PublicCaptchaField
              action="chatbot_public_query"
              onTokenChange={setPublicCaptchaToken}
              resetTrigger={publicCaptchaResetTrigger}
            />
          </div>
        )}
        <p className="chatbot-disclaimer" role="note">
          {CHATBOT_LABELS.disclaimer}
        </p>
      </div>
    </div>
  );
};
